import { useEffect, useState } from "react";
import { Battery, Wifi, MicOff } from "lucide-react";

/**
 * Animated Rammy robot face.
 * expressions: idle | listening | thinking | speaking | sleeping
 *              | happy | sad | curious | excited
 */
export default function RobotFace({
  expression = "idle",
  showHud = true,
  battery = 78,
  wifi = true,
  micMuted = false,
  time = null,
  weather = null,
  size = 360,
  className = "",
  round = false,
  onClick,
}) {
  // Trigger a soft transition when expression changes
  const [state, setState] = useState(expression);
  useEffect(() => {
    const t = setTimeout(() => setState(expression), 40);
    return () => clearTimeout(t);
  }, [expression]);

  const eyeStyle = getEyeStyle(state);
  const mouth = getMouth(state);
  const eyeAnim = getEyeAnim(state);

  return (
    <div
      onClick={onClick}
      className={`rammy-face relative select-none ${round ? "rounded-full" : "rounded-[2.5rem]"} ${className}`}
      style={{ width: size, height: size, cursor: onClick ? "pointer" : "default" }}
      data-testid="robot-face-display"
      data-expression={state}
    >
      {/* HUD */}
      {showHud && (
        <div className="absolute top-0 left-0 right-0 px-5 pt-4 flex items-center justify-between text-[10px] font-mono text-[#8ea6a1] z-10">
          <div className="flex items-center gap-2">
            <span data-testid="robot-time">{time ?? currentTime()}</span>
            {weather && <span className="opacity-70">{weather}</span>}
          </div>
          <div className="flex items-center gap-2">
            {micMuted && <MicOff size={11} className="text-[#F7A072]" />}
            <Wifi size={11} className={wifi ? "text-[#50E3C2]" : "text-[#8ea6a1] opacity-50"} />
            <span className="flex items-center gap-1">
              <Battery size={11} />
              <span>{battery}%</span>
            </span>
          </div>
        </div>
      )}

      {/* Eyes + mouth container */}
      <div className={`absolute inset-0 flex items-center justify-center ${state === "sleeping" ? "" : "breathe"}`}>
        <div className={`flex flex-col items-center ${eyeAnim.container}`}>
          <div className="flex items-center gap-8" style={{ marginBottom: mouth.mb }}>
            <div
              className={`eye ${eyeAnim.blink ? "blink" : ""}`}
              style={{ width: eyeStyle.w, height: eyeStyle.h, borderRadius: eyeStyle.br, transform: eyeStyle.leftTx, background: eyeStyle.color, boxShadow: eyeStyle.glow }}
              data-testid="robot-eye-left"
            />
            <div
              className={`eye ${eyeAnim.blink ? "blink" : ""}`}
              style={{ width: eyeStyle.w, height: eyeStyle.h, borderRadius: eyeStyle.br, transform: eyeStyle.rightTx, background: eyeStyle.color, boxShadow: eyeStyle.glow }}
              data-testid="robot-eye-right"
            />
          </div>
          {mouth.node}
        </div>
      </div>

      {/* Zzz for sleeping */}
      {state === "sleeping" && (
        <div className="absolute top-14 right-14 text-[#50E3C2]/80 font-display text-2xl zzz">z</div>
      )}
    </div>
  );
}

function currentTime() {
  const d = new Date();
  const h = d.getHours().toString().padStart(2, "0");
  const m = d.getMinutes().toString().padStart(2, "0");
  return `${h}:${m}`;
}

function getEyeStyle(s) {
  const base = { w: 56, h: 56, br: "22%", color: "#50E3C2", glow: "0 0 14px rgba(80,227,194,0.55), 0 0 30px rgba(80,227,194,0.35)", leftTx: "none", rightTx: "none" };
  switch (s) {
    case "happy":
      return { ...base, h: 30, br: "40% 40% 6px 6px / 60% 60% 6px 6px" };
    case "excited":
      return { ...base, w: 66, h: 66, color: "#7FF0D9" };
    case "sad":
      return { ...base, h: 42, br: "6px 6px 40% 40% / 6px 6px 60% 60%", color: "#79C2E8" };
    case "curious":
      return { ...base, w: 58, h: 62, leftTx: "translateY(-6px) rotate(-6deg)", rightTx: "translateY(4px) rotate(6deg)" };
    case "listening":
      return { ...base, w: 60, h: 60, color: "#50E3C2" };
    case "thinking":
      return { ...base, w: 50, h: 50, color: "#8FE6D8" };
    case "speaking":
      return { ...base, h: 34, br: "40% 40% 8px 8px / 60% 60% 8px 8px" };
    case "sleeping":
      return { ...base, h: 6, br: "4px", color: "#2c5a52", glow: "0 0 4px rgba(80,227,194,0.25)" };
    default:
      return base;
  }
}

function getEyeAnim(s) {
  switch (s) {
    case "thinking":
      return { blink: false, container: "think-scan" };
    case "sleeping":
      return { blink: false, container: "" };
    case "speaking":
      return { blink: true, container: "" };
    case "listening":
      return { blink: false, container: "" };
    default:
      return { blink: true, container: "" };
  }
}

function getMouth(s) {
  const cyan = "#50E3C2";
  const glow = "0 0 12px rgba(80,227,194,0.55)";
  const mb = 24;
  switch (s) {
    case "happy":
    case "excited":
      return {
        mb,
        node: (
          <div style={{
            width: 90, height: 30,
            borderBottomLeftRadius: 60, borderBottomRightRadius: 60,
            background: cyan, boxShadow: glow, marginTop: 18,
          }} data-testid="robot-mouth"/>
        ),
      };
    case "sad":
      return {
        mb,
        node: (
          <div style={{
            width: 90, height: 26,
            borderTopLeftRadius: 60, borderTopRightRadius: 60,
            background: "#79C2E8", boxShadow: glow, marginTop: 28,
          }} data-testid="robot-mouth"/>
        ),
      };
    case "curious":
      return {
        mb,
        node: (
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: cyan, boxShadow: glow, marginTop: 18,
          }} data-testid="robot-mouth"/>
        ),
      };
    case "speaking":
      return {
        mb,
        node: (
          <div className="speak-mouth" style={{
            width: 70, height: 22, borderRadius: 20,
            background: cyan, boxShadow: glow, marginTop: 22,
          }} data-testid="robot-mouth"/>
        ),
      };
    case "listening":
      return {
        mb,
        node: (
          <div className="flex items-end gap-2 mt-6" data-testid="robot-mouth">
            {[8, 16, 24, 16, 8].map((h, i) => (
              <div key={i} className="rounded-full" style={{
                width: 6, height: h, background: cyan, boxShadow: glow,
                animation: `speak-mouth ${0.5 + i * 0.05}s ease-in-out infinite`,
              }} />
            ))}
          </div>
        ),
      };
    case "thinking":
      return {
        mb,
        node: (
          <div className="flex items-center gap-2 mt-8" data-testid="robot-mouth">
            {[0, 0.15, 0.3].map((d, i) => (
              <div key={i} className="rounded-full" style={{
                width: 8, height: 8, background: cyan, boxShadow: glow,
                animation: `blink 1s infinite`,
                animationDelay: `${d}s`,
              }} />
            ))}
          </div>
        ),
      };
    case "sleeping":
      return {
        mb: 20,
        node: (
          <div style={{
            width: 40, height: 4, borderRadius: 4,
            background: "#2c5a52", marginTop: 24,
          }} data-testid="robot-mouth"/>
        ),
      };
    default:
      return {
        mb: 20,
        node: (
          <div style={{
            width: 60, height: 20,
            borderBottomLeftRadius: 30, borderBottomRightRadius: 30,
            background: cyan, boxShadow: glow, marginTop: 22,
          }} data-testid="robot-mouth"/>
        ),
      };
  }
}
